import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import "./ModalDevice.css";

Modal.setAppElement("#root");
const API_BASE = process.env.REACT_APP_API_BASE_URL;

// ─── Catálogos ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: "1", label: "Activo"   },
  { value: "0", label: "Inactivo" },
];

const TYPE_OPTIONS = [
  { value: "0", label: "Basic"   },
  { value: "1", label: "Premium" },
];

const TANK_TYPE_OPTIONS = [
  { value: "A", label: "Tipo A" },
  { value: "B", label: "Tipo B" },
  { value: "C", label: "Tipo C" },
];

const INSTALLATION_TYPE_OPTIONS = [
  { value: "escuela", label: "Escuela" },
  { value: "hotel",   label: "Hotel"   },
  { value: "otro",    label: "Otro"    },
];

// Catálogo cerrado de 32 entidades federativas — necesario para que el
// portafolio institucional pueda agrupar/comparar por estado de forma
// consistente (texto libre produciría "Guerrero"/"guerrero"/"GRO" como si
// fueran 3 grupos distintos).
const ESTADO_OPTIONS = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
  "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
  "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
  "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
  "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán",
  "Zacatecas",
];

// ─── Envío de configuración al chip (firmware V2) ────────────────────────────
// Campos del formulario que el chip entiende → ruta en cmd.set_config
// (PROTOCOL.md §6.1, data AGRUPADO). Solo estos 3 se arman solos; el resto
// de claves del protocolo se pueden escribir a mano en el cuadro.
const CONFIG_FIELD_MAP = {
  tank_height:     { group: "tank", key: "tank_height_m"    },
  tank_capacity_l: { group: "tank", key: "tank_capacity_l"  },
  roof_area_m2:    { group: "rain", key: "catchment_area_m2" },
};

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const isV2 = (v) => parseInt(String(v ?? "1"), 10) >= 2;

// Actualiza SOLO la clave del campo que cambió, conservando cualquier otra
// edición manual que el usuario haya hecho en el cuadro. Si el valor vuelve
// al original (o queda vacío), esa clave se retira del JSON.
const applyFieldToJson = (prevText, field, value, deviceData) => {
  const map = CONFIG_FIELD_MAP[field];
  if (!map) return prevText;

  let msg = null;
  try { msg = JSON.parse(prevText); } catch { msg = null; }
  if (
    !msg || typeof msg !== "object" || Array.isArray(msg) ||
    typeof msg.data !== "object" || msg.data === null || Array.isArray(msg.data)
  ) {
    msg = { type: "cmd.set_config", data: {} };
  }
  const data = msg.data;

  const cur  = toNum(value);
  const orig = toNum(deviceData?.[field]);
  if (cur !== null && cur !== orig) {
    if (typeof data[map.group] !== "object" || data[map.group] === null) data[map.group] = {};
    data[map.group][map.key] = cur;
  } else if (data[map.group] && typeof data[map.group] === "object") {
    delete data[map.group][map.key];
    if (!Object.keys(data[map.group]).length) delete data[map.group];
  }

  return Object.keys(data).length
    ? JSON.stringify({ type: "cmd.set_config", data }, null, 2)
    : "";
};

// ─── Estado inicial — sin keycode (se genera en el servidor) ──────────────────
const initialFormState = {
  id_user:              "",
  latitude:             "",
  longitude:            "",
  status:               "1",
  roof_area_m2:         "",
  tank_capacity_l:      "",
  tank_type:            "A",
  tank_height:          "",
  num_tanques:          "1",
  type:                 "0",
  next_payment_date:    "",
  operation_start_date: "",
  id_donor:             "",
  installation_name:    "",
  installation_type:    "escuela",
  estado:               "",
  municipio:            "",
  hp:                   "1",
  firmware_version:     "1",
};

// ══════════════════════════════════════════════════════════════════════════════
// Sub-componente: Modal de búsqueda de usuario — reutilizable para
// propietario Y donante (mismo origen de datos: tb_users).
// ══════════════════════════════════════════════════════════════════════════════
const MIN_CHARS   = 2;
const MAX_RESULTS = 20;

const UserSearchModal = ({ isOpen, onClose, onSelect, title = "Buscar Propietario" }) => {
  const [users,      setUsers]      = useState([]);
  const [filterText, setFilterText] = useState("");
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFilterText("");
    setLoading(true);
    fetch(`${API_BASE}/api/getUsers?status=1`)
      .then((res) => res.json())
      .then((data) => { if (data.success) setUsers(data.users); })
      .catch((err) => console.error("Error fetching users:", err))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const isTyping  = filterText.trim().length > 0;
  const hasEnough = filterText.trim().length >= MIN_CHARS;
  const filtered  = hasEnough
    ? users
        .filter((u) => `${u.name} ${u.username}`.toLowerCase().includes(filterText.toLowerCase()))
        .slice(0, MAX_RESULTS)
    : [];

  const renderList = () => {
    if (loading)    return <p className="user-search-hint">Cargando usuarios...</p>;
    if (!isTyping)  return <p className="user-search-hint">Escribe para buscar un usuario.</p>;
    if (!hasEnough) return <p className="user-search-hint">Escribe al menos {MIN_CHARS} caracteres.</p>;
    if (!filtered.length)
      return <p className="user-search-hint user-search-hint--empty">Sin resultados para <strong>"{filterText}"</strong>.</p>;
    return (
      <ul className="user-search-list">
        {filtered.map((u, idx) => (
          <li key={u.id_user}
            className={`user-search-item ${idx % 2 === 0 ? "user-search-item--even" : ""}`}
            onClick={() => onSelect(u)}
          >
            <div className="user-search-item-info">
              <span className="user-search-name">{u.name}</span>
              <span className="user-search-username">@{u.username}</span>
            </div>
            <span className="user-search-select-hint">Seleccionar →</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}
      className="modal-content user-search-modal"
      overlayClassName="modal-overlay user-search-overlay"
    >
      <div className="modal-header"><h2>{title}</h2><hr /></div>
      <div className="modal-body">
        <input type="text" className="search-input user-search-input"
          placeholder="Nombre o usuario..." value={filterText}
          onChange={(e) => setFilterText(e.target.value)} autoFocus
        />
        <div className="user-search-results">{renderList()}</div>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
      </div>
    </Modal>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Componente principal: ModalDevice
// ══════════════════════════════════════════════════════════════════════════════
const ModalDevice = ({ isOpen, onRequestClose, deviceData, isEditMode }) => {
  const [formData,          setFormData]          = useState(initialFormState);
  const [selectedUserName,  setSelectedUserName]  = useState("");
  const [selectedDonorName, setSelectedDonorName] = useState("");
  const [isUserSearchOpen,  setIsUserSearchOpen]  = useState(false);
  const [isDonorSearchOpen, setIsDonorSearchOpen] = useState(false);
  const [errorMessage,      setErrorMessage]      = useState("");
  const [processMessage,    setProcessMessage]    = useState("");
  const [processType,       setProcessType]       = useState("");

  // Panel "JSON al chip" (solo edición + firmware V2)
  const [jsonText,    setJsonText]    = useState("");
  const [sending,     setSending]     = useState(false);
  const [sendMsg,     setSendMsg]     = useState("");
  const [sendType,    setSendType]    = useState("");
  const [pendingClose, setPendingClose] = useState(false); // ya se guardó en BD; falta decidir sobre el JSON

  // ── Rellena / limpia el formulario ────────────────────────────────────────
  useEffect(() => {
    if (deviceData) {
      setFormData({
        id_user:              deviceData.id_user    !== undefined ? String(deviceData.id_user) : "",
        latitude:             deviceData.latitude   || "",
        longitude:            deviceData.longitude  || "",
        status:               deviceData.status     !== undefined ? String(deviceData.status)  : "1",
        roof_area_m2:         deviceData.roof_area_m2    || "",
        tank_capacity_l:      deviceData.tank_capacity_l || "",
        tank_type:            deviceData.tank_type        || "A",
        tank_height:          deviceData.tank_height      || "",
        num_tanques:          deviceData.num_tanques      ? String(deviceData.num_tanques) : "1",
        type:                 deviceData.type        !== undefined ? String(deviceData.type)   : "0",
        next_payment_date:    deviceData.next_payment_date    || "",
        operation_start_date: deviceData.operation_start_date || "",
        id_donor:             deviceData.id_donor    ? String(deviceData.id_donor) : "",
        installation_name:    deviceData.installation_name || "",
        installation_type:    deviceData.installation_type || "escuela",
        estado:               deviceData.estado             || "",
        municipio:            deviceData.municipio          || "",
        hp:                   deviceData.hp ? String(deviceData.hp) : "1",
        firmware_version:     deviceData.firmware_version ? String(deviceData.firmware_version) : "1",
      });
      setSelectedUserName(deviceData.user_name || "");
      setSelectedDonorName(deviceData.donor_name || "");
      setJsonText(""); setSendMsg(""); setSendType(""); setPendingClose(false);
    } else {
      setFormData(initialFormState);
      setSelectedUserName("");
      setSelectedDonorName("");
    }
  }, [deviceData, isOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (isEditMode && CONFIG_FIELD_MAP[name]) {
      setJsonText((prev) => applyFieldToJson(prev, name, value, deviceData));
      setSendMsg("");
    }
  };

  // ── Panel JSON → chip ─────────────────────────────────────────────────────
  let jsonError = "";
  let jsonParsed = null;
  if (jsonText.trim()) {
    try {
      jsonParsed = JSON.parse(jsonText);
      if (jsonParsed?.type !== "cmd.set_config" || typeof jsonParsed?.data !== "object" || jsonParsed.data === null) {
        jsonError = 'El JSON debe tener "type": "cmd.set_config" y un objeto "data".';
      }
    } catch (err) {
      jsonError = `JSON inválido: ${err.message}`;
    }
  }
  const savedIsV2   = isV2(deviceData?.firmware_version);
  const canSendJson = !!jsonText.trim() && !jsonError && savedIsV2 && !sending;

  const handleDiscardJson = () => {
    setJsonText("");
    setSendMsg("");
    if (pendingClose) resetAndClose();
  };

  const handleSendJson = () => {
    if (!canSendJson || !deviceData?.id_device) return;
    setSending(true);
    setSendMsg("");
    fetch(`${API_BASE}/api/sendDeviceConfig`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ id_device: deviceData.id_device, message: jsonParsed }),
    })
      .then((res) => res.json().catch(() => ({ success: false, message: "Respuesta inválida del servidor." })))
      .then((data) => {
        setSendType(data.success ? "success" : "error");
        setSendMsg(data.success ? "JSON enviado al chip." : (data.message || "No se pudo enviar."));
        if (data.success) {
          setJsonText("");
          if (pendingClose) setTimeout(() => resetAndClose(), 1200);
        }
      })
      .catch(() => { setSendType("error"); setSendMsg("No se pudo contactar al servidor."); })
      .finally(() => setSending(false));
  };

  const handleUserSelect = (user) => {
    setFormData((prev) => ({ ...prev, id_user: String(user.id_user) }));
    setSelectedUserName(user.name);
    setIsUserSearchOpen(false);
  };

  const handleDonorSelect = (user) => {
    setFormData((prev) => ({ ...prev, id_donor: String(user.id_user) }));
    setSelectedDonorName(user.name);
    setIsDonorSearchOpen(false);
  };

  const handleDonorClear = () => {
    setFormData((prev) => ({ ...prev, id_donor: "" }));
    setSelectedDonorName("");
  };

  const resetAndClose = () => {
    setFormData(initialFormState);
    setSelectedUserName("");
    setSelectedDonorName("");
    setProcessMessage("");
    setProcessType("");
    setJsonText(""); setSendMsg(""); setSendType(""); setPendingClose(false);
    onRequestClose();
  };

  const handleFormSubmit = (e) => {
    e?.preventDefault();

    if (!formData.id_user) {
      setErrorMessage("El propietario es obligatorio.");
      return;
    }
    if (formData.status !== "0" && formData.status !== "1") {
      setErrorMessage("Selecciona un estatus válido.");
      return;
    }
    if (formData.type !== "0" && formData.type !== "1") {
      setErrorMessage("Selecciona un plan válido.");
      return;
    }
    if (!["A", "B", "C"].includes(formData.tank_type)) {
      setErrorMessage("Selecciona un tipo de tanque válido.");
      return;
    }
    setErrorMessage("");

    const url = isEditMode
      ? `${API_BASE}/api/updateDevice`
      : `${API_BASE}/api/insertDevice`;

    // Keycode NO se envía — el servidor lo genera en insert,
    // y lo ignora (no lo toca) en update.
    const payload = {
      formData: {
        id_user:              Number(formData.id_user),
        latitude:             formData.latitude.trim(),
        longitude:            formData.longitude.trim(),
        status:               Number(formData.status),
        roof_area_m2:         formData.roof_area_m2.trim(),
        tank_capacity_l:      formData.tank_capacity_l.trim(),
        tank_type:            formData.tank_type,
        tank_height:          formData.tank_height.trim(),
        num_tanques:          formData.num_tanques.trim() || "1",
        type:                 Number(formData.type),
        next_payment_date:    formData.next_payment_date.trim(),
        operation_start_date: formData.operation_start_date.trim(),
        // Donante es OPCIONAL — id_donor vacío significa "sin donante".
        id_donor:             formData.id_donor ? Number(formData.id_donor) : "",
        installation_name:    formData.installation_name.trim(),
        installation_type:    formData.installation_type,
        estado:               formData.estado,
        municipio:            formData.municipio.trim(),
        hp:                   formData.hp.trim() || "1",
        firmware_version:     formData.firmware_version || "1",
      },
    };

    if (isEditMode && deviceData?.id_device) {
      payload.id_device = deviceData.id_device;
    }

    fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!isEditMode && data.success) {
          /* Confirmación de alta: el modal NO se cierra hasta que el usuario
             haga clic en Aceptar. El keycode generado se muestra claramente. */
          return Swal.fire({
            title: "¡Dispositivo registrado!",
            html: `
              <p style="margin-bottom:0.5em">El dispositivo fue creado exitosamente.</p>
              <p style="font-size:0.9rem;color:#555;margin-bottom:0.75em">
                Guarda este keycode — lo necesitarás para configurar el chip.
              </p>
              <div style="
                display:inline-block;
                padding:0.5em 1.2em;
                background:#eaf3ff;
                border:2px solid #0053ae;
                border-radius:8px;
                font-size:1.25rem;
                font-weight:700;
                color:#0053ae;
                letter-spacing:0.06em;
              ">${data.keycode}</div>
            `,
            icon: "success",
            confirmButtonText: "Entendido",
            confirmButtonColor: "#0053ae",
            allowOutsideClick: false,
            allowEscapeKey:    false,
          }).then(() => resetAndClose());
        }

        // Edición V2 con JSON pendiente: se guardó en BD, pero el modal NO se
        // cierra hasta decidir qué hacer con el JSON (enviar / no enviar).
        if (isEditMode && data.success && isV2(formData.firmware_version) && jsonText.trim()) {
          setProcessType("success");
          setProcessMessage("Guardado. Revisa el JSON y envíalo al chip, o pulsa «No enviar».");
          setPendingClose(true);
          return;
        }

        setProcessType(data.success ? "success" : "error");
        setProcessMessage(data.message || (data.success ? "Guardado." : "Error al guardar."));
        setTimeout(() => resetAndClose(), 1200);
      })
      .catch(() => {
        setProcessType("error");
        setProcessMessage("Error al procesar.");
        setTimeout(() => resetAndClose(), 1200);
      });
  };

  const handleCloseModal = () => {
    setFormData(initialFormState);
    setSelectedUserName("");
    setSelectedDonorName("");
    setErrorMessage("");
    setProcessMessage("");
    setProcessType("");
    setJsonText(""); setSendMsg(""); setSendType(""); setPendingClose(false);
    onRequestClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal isOpen={isOpen} onRequestClose={handleCloseModal}
        className="modal-content" overlayClassName="modal-overlay"
      >
        {/* Header */}
        <div className="modal-header">
          <h2>{isEditMode ? "Editar Dispositivo" : "Agregar Dispositivo"}</h2>
          <hr />
        </div>

        {/* Body */}
        <div className="modal-body">
          {errorMessage && <p className="error-label">{errorMessage}</p>}

          {/* Keycode — solo visible en modo edición, solo lectura */}
          {isEditMode && deviceData?.keycode && (
            <div className="form-row">
              <div className="form-group form-group--full">
                <label>Keycode del dispositivo</label>
                <div className="device-keycode-badge">{deviceData.keycode}</div>
              </div>
            </div>
          )}

          <form onSubmit={handleFormSubmit}>

            {/* Fila 1 — Nombre de la instalación / Perfil */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="installation_name">Nombre de la instalación</label>
                <input type="text" id="installation_name" name="installation_name"
                  value={formData.installation_name} onChange={handleInputChange}
                  placeholder="Ej. Escuela Primaria Benito Juárez"
                />
              </div>
              <div className="form-group">
                <label htmlFor="installation_type">Perfil</label>
                <select id="installation_type" name="installation_type"
                  value={formData.installation_type} onChange={handleInputChange}>
                  {INSTALLATION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Fila 2 — Estado / Municipio */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="estado">Estado</label>
                <select id="estado" name="estado" value={formData.estado}
                  onChange={handleInputChange}>
                  <option value="">Sin especificar</option>
                  {ESTADO_OPTIONS.map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="municipio">Municipio</label>
                <input type="text" id="municipio" name="municipio"
                  value={formData.municipio} onChange={handleInputChange}
                  placeholder="Ej. Acapulco de Juárez"
                />
              </div>
            </div>

            {/* Fila 3 — Estatus / Plan */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Estatus</label>
                <select id="status" name="status" value={formData.status}
                  onChange={handleInputChange} required>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="type">Plan</label>
                <select id="type" name="type" value={formData.type}
                  onChange={handleInputChange} required>
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Fila 4 — Propietario */}
            <div className="form-row">
              <div className="form-group form-group--full">
                <label>Propietario</label>
                <div className="user-selector">
                  <input type="text" className="user-selector-display"
                    value={selectedUserName} placeholder="Sin propietario seleccionado" readOnly
                  />
                  <button type="button" className="btn btn-secondary user-selector-btn"
                    onClick={() => setIsUserSearchOpen(true)}>
                    Buscar
                  </button>
                </div>
              </div>
            </div>

            {/* Fila 5 — Donante (opcional, distinto del propietario) */}
            <div className="form-row">
              <div className="form-group form-group--full">
                <label>
                  Donante <span className="form-label-hint">(opcional — quién dona o paga el dispositivo)</span>
                </label>
                <div className="user-selector">
                  <input type="text" className="user-selector-display"
                    value={selectedDonorName} placeholder="Sin donante (igual al propietario)" readOnly
                  />
                  <button type="button" className="btn btn-secondary user-selector-btn"
                    onClick={() => setIsDonorSearchOpen(true)}>
                    Buscar
                  </button>
                  {selectedDonorName && (
                    <button type="button" className="btn btn-secondary user-selector-btn"
                      onClick={handleDonorClear} title="Quitar donante">
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Fila 6 — Latitud / Longitud */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="latitude">Latitud</label>
                <input type="text" id="latitude" name="latitude"
                  value={formData.latitude} onChange={handleInputChange}
                  placeholder="Ej. 19.432608"
                />
              </div>
              <div className="form-group">
                <label htmlFor="longitude">Longitud</label>
                <input type="text" id="longitude" name="longitude"
                  value={formData.longitude} onChange={handleInputChange}
                  placeholder="Ej. -99.133209"
                />
              </div>
            </div>

            {/* Fila 7 — Superficie / Capacidad */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="roof_area_m2">Superficie del techo (m²)</label>
                <input type="number" id="roof_area_m2" name="roof_area_m2"
                  value={formData.roof_area_m2} onChange={handleInputChange}
                  placeholder="Ej. 45" min="0" step="0.01"
                />
              </div>
              <div className="form-group">
                <label htmlFor="tank_capacity_l">Capacidad instalada (L)</label>
                <input type="number" id="tank_capacity_l" name="tank_capacity_l"
                  value={formData.tank_capacity_l} onChange={handleInputChange}
                  placeholder="Ej. 1000" min="0" step="1"
                />
              </div>
            </div>

            {/* Fila 8 — Tipo de tanque / Altura del tanque */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="tank_type">Tipo de tanque</label>
                <select id="tank_type" name="tank_type" value={formData.tank_type}
                  onChange={handleInputChange} required>
                  {TANK_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="tank_height">Altura del tanque (m)</label>
                <input type="number" id="tank_height" name="tank_height"
                  value={formData.tank_height} onChange={handleInputChange}
                  placeholder="Ej. 2" min="0" step="0.01"
                />
              </div>
            </div>

            {/* Fila 8.5 — HP de la bomba instalada (futuras fórmulas de
                cálculo) / Versión de firmware del chip en sitio.
                firmware_version habilita el panel "JSON al chip" (solo
                versión 2+) — los chips V1 (legacy, default) NUNCA
                reciben comandos. */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="hp">HP de la bomba</label>
                <input type="number" id="hp" name="hp"
                  value={formData.hp} onChange={handleInputChange}
                  placeholder="Ej. 1" min="0" step="0.1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="firmware_version">Versión de firmware</label>
                <select id="firmware_version" name="firmware_version"
                  value={formData.firmware_version} onChange={handleInputChange}
                >
                  <option value="1">V1 (legacy)</option>
                  <option value="2">V2 (TEFNUT — Alfredo)</option>
                </select>
              </div>
            </div>

            {/* Fila 9 — Próximo pago / Inicio de operación */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="next_payment_date">Próximo pago</label>
                <input type="date" id="next_payment_date" name="next_payment_date"
                  value={formData.next_payment_date} onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="operation_start_date">Inicio de operación</label>
                <input type="date" id="operation_start_date" name="operation_start_date"
                  value={formData.operation_start_date} onChange={handleInputChange}
                />
              </div>
            </div>

          </form>

          {/* Panel — JSON a enviar al chip (solo edición + firmware V2).
              Se arma solo con los campos que cambien (altura, capacidad,
              superficie); es editable. Guardar en BD y enviar al chip son
              acciones independientes. */}
          {isEditMode && isV2(formData.firmware_version) && (
            <div className="cfg-json-panel">
              <label htmlFor="cfg_json">
                JSON a enviar al chip{" "}
                <span className="form-label-hint">
                  (se arma solo al cambiar altura, capacidad o superficie; puedes editarlo)
                </span>
              </label>
              <textarea
                id="cfg_json"
                className={`cfg-json-text ${jsonError ? "cfg-json-text--error" : ""}`}
                rows={9}
                spellCheck={false}
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); setSendMsg(""); }}
                placeholder={'Sin cambios para enviar.\nModifica un campo del formulario y el JSON aparecerá aquí.'}
              />
              {jsonError && <p className="error-label">{jsonError}</p>}
              {!savedIsV2 && (
                <p className="cfg-json-hint">
                  Este dispositivo aún está guardado como V1: guarda primero la versión V2 para poder enviar.
                </p>
              )}
              {sendMsg && <p className={`cfg-json-msg ${sendType}`}>{sendMsg}</p>}
              <div className="cfg-json-actions">
                <button type="button" className="btn btn-secondary"
                  onClick={handleDiscardJson} disabled={!jsonText.trim() && !pendingClose}>
                  No enviar
                </button>
                <button type="button" className="btn btn-primary"
                  onClick={handleSendJson} disabled={!canSendJson}>
                  {sending ? "Enviando..." : "Enviar al chip"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
            {pendingClose ? "Cerrar" : "Cancelar"}
          </button>
          <button type="button" className="btn btn-success" onClick={handleFormSubmit}
            disabled={pendingClose}>
            {isEditMode ? "Guardar" : "Agregar"}
          </button>
        </div>

        {processMessage && (
          <p className={`process-message ${processType}`}>{processMessage}</p>
        )}
      </Modal>

      {/* Modal secundario — buscador de propietario */}
      <UserSearchModal
        isOpen={isUserSearchOpen}
        onClose={() => setIsUserSearchOpen(false)}
        onSelect={handleUserSelect}
        title="Buscar Propietario"
      />

      {/* Modal secundario — buscador de donante */}
      <UserSearchModal
        isOpen={isDonorSearchOpen}
        onClose={() => setIsDonorSearchOpen(false)}
        onSelect={handleDonorSelect}
        title="Buscar Donante"
      />
    </>
  );
};

export default ModalDevice;